<script setup lang="ts">
  defineOptions({
    name: 'ChatDateDivider',
  })

  import { computed } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { ChevronDownIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { dayjs } from '@common/core-layer/utils/dayjs'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPText from '@ui/components/CPText.vue'

  const props = defineProps({
    date: {
      type: String,
      required: true,
    },
  })

  const { t, locale } = useI18n()

  const parseDate = (value: string): Date | null => {
    if (!value) return null

    // Parse date-only keys strictly as local calendar dates.
    const dateOnly = dayjs(value, 'YYYY-MM-DD', true)
    if (dateOnly.isValid()) {
      return dateOnly.toDate()
    }

    const parsedDate = dayjs(value)
    return parsedDate.isValid() ? parsedDate.toDate() : null
  }

  // Format the date for display
  const formattedDate = computed(() => {
    if (!props.date) return t('chats.date.today')

    const dateObject = parseDate(props.date)
    if (!dateObject) return t('chats.date.today')

    const dayDifference = dayjs().startOf('day').diff(dayjs(dateObject).startOf('day'), 'day')

    if (dayDifference === 0) {
      return t('chats.date.today')
    } else if (dayDifference === 1) {
      return t('chats.date.yesterday')
    } else if (dayDifference < 7) {
      return new Intl.DateTimeFormat(locale.value, { weekday: 'long' }).format(dateObject)
    } else {
      return new Intl.DateTimeFormat(locale.value, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(dateObject)
    }
  })

  const dateId = computed(() => String(props.date || 'unknown').replace(/[^a-zA-Z0-9]/g, '-'))
</script>

<template>
  <CPDivider :id="`chat-date-divider-${dateId}`" class-name="py-4" margin-top="0px" margin-bottom="0px">
    <div
      :id="`chat-date-divider-content-${dateId}`"
      class="divider-content flex items-center gap-1 rounded-full border border-transparent bg-primary px-3 py-1"
    >
      <CPText size="xs" class="!text-on-primary">
        {{ formattedDate }}
      </CPText>
      <ChevronDownIcon :id="`chat-date-divider-icon-${dateId}`" class="h-3 w-3 text-on-primary" />
    </div>
  </CPDivider>
</template>

<style scoped>
  .divider-content {
    margin: 0 12px;
  }
</style>
